import { useEffect, useMemo, useState } from 'react';
import { RootState } from '../../../logic/store/Store';
import { useDispatch, useSelector } from 'react-redux';
import { clone, cloneDeep, groupBy, keyBy, orderBy, uniq } from 'lodash';
import { setDerivedData } from '../../../logic/store/Plans/FieldPlan/FieldPlanSlice';
import { AvailabilityEnum, IProductIdentifier } from './Logic/IProductReference';
import { ICropPlanFullResponse } from '../../../logic/Models/Responses/FieldPlanResponse';
import { CropConfig } from '../../../logic/store/Seeds/CropsSlice';
import { FieldPlanProductColorsList } from '../../../logic/Utility/Colors';
import { getSeedsForGrower } from '../../../logic/store/Seeds/SeedsActions';
import { CalculateBags, RoundAcres } from '../../../logic/Utility/CalculationUtilities';
import { autoSelectTreatmentForHybrid, hydrateHybrid } from '../../../logic/store/Seeds/SeedLookups';

/**
 * Compare if two products are the same (by hybrid and treatment)
 */
export function matchProductIdentity(a: IProductIdentifier, b: IProductIdentifier): boolean 
{
	return a.HybridId === b.HybridId
		&& a.TreatmentId === b.TreatmentId;
}

/**
 * Compare if a product selection matches a product identifier.
 */
export function matchAssignmentToProduct(selected: { HybridId?: string, TreatmentId?: string}, product: IProductIdentifier): boolean
{
	return selected.HybridId === product.HybridId 
		&& ((product.TreatmentId !== undefined && product.TreatmentId === selected.TreatmentId));
}

/**
 * This hook tracks crop plan data and assigns derived data -- the summary list, available products, which default products to use, etc.
 */
export const useUpdateFieldPlanProductDerivedData = () => 
{
	const dispatch = useDispatch();
	const cropPlan: ICropPlanFullResponse | undefined = useSelector((state: RootState) => state.fieldplan.fullCropPlanData?.find(plan => plan.Id === state.fieldplan.selectedCropPlanId));
	const cropId = cropPlan?.CropId;
	const brandApplication = cropPlan?.Settings?.BrandApplication;

	// Fetch all data we need to build our state if we don't have it already
	const seeds = useSelector((state: RootState) => state.seeds.allSeedsV7);
	const seriesLookup = useSelector((state: RootState) => state.seeds.series);
	const hybridLookup = useSelector((state: RootState) => state.seeds.hybrids);
	const brandLookup = useSelector((state: RootState) => state.seeds.brands);
	const treatmentLookup = useSelector((state: RootState) => state.seeds.treatments);
	const materialLookup = useSelector((state: RootState) => state.seeds.materials);
	const isLoadingSeeds = useSelector((state: RootState) => state.seeds.isLoading);
	const [error, setError] = useState<Error | null>(null);

	useEffect(() => 
	{
		// Don't download seeds twice if we're already in the process of loading them
		if(!seeds?.length && !isLoadingSeeds)
		{
			dispatch(getSeedsForGrower());
		}
	}, [seeds?.length, dispatch]);

	// calculate all available products
	// Get a list of all products in consideration at the 'top' level (crop and brand)
	const products = useMemo(() => (!cropId || !brandApplication || !seeds) ? [] 
		: seeds.filter(seedLevel => brandApplication === 'Enogen' ?
			seedLevel.CropId === cropId && seedLevel.Brand.toLowerCase().includes('enogen')
			: seedLevel.CropId === cropId && !seedLevel.Brand.toLowerCase().includes('enogen'))
			.flatMap(brandLevel => Object.values(brandLevel.Series))
			// Leave out any hybrids that are not available
			.flatMap(seriesLevel => Object.values(seriesLevel.Hybrids).filter(h => h.Availability !== 'None' && h.Availability !== 'Unknown'))
			.flatMap(hybrid => 
			{
				const hybridTreatments = clone(Object.values(hybrid.Treatments));
				const hybridSeries = seriesLookup[hybridLookup[hybrid.HybridId].SeriesId];

				// Cross-combine with available treatments
				const productsWithBuiltInTreatments = hybridTreatments.filter(t => t.Availability !== 'None' && t.Availability !== 'Unknown')
					.map<IProductIdentifier>(t => ({
						CropId: cropId,
						SeriesId: hybridSeries.SeriesId,
						SeriesName: hybridSeries.SeriesName,
						HybridId: hybrid.HybridId,
						HybridName: hybrid.HybridName,
						TraitId: hybrid.TraitId,
						TraitName: hybrid.TraitName,
						TraitFullName: hybrid.TraitFullName,
						TreatmentName: t.TreatmentName,
						TreatmentId: t.TreatmentId,
						Availability: hybrid.Availability as AvailabilityEnum,
						RelativeMaturity: parseFloat(hybridSeries.RelativeMaturity)
					}));

				return [...productsWithBuiltInTreatments];
			}) ?? [],
	[seeds, cropId, brandApplication]);

	// This is a map of series to the product that should be used if the series is selected.
	const recommendedProducts = useMemo(() => 
	{
		if (!cropPlan) 
		{
			return {};
		}

		return keyBy(cropPlan.SelectedProducts.map<IProductIdentifier | undefined>(sp =>
		// Map to our loaded products 
		// TODO: Most expensive & available. 99% of the time this is fine
		// because the user only picks one version through the UI anyway.
		{
			try 
			{
				// Clone the selected product so we can change the treatment if needed
				const clonedSp = clone(sp);
				const hybridLookupItem = hybridLookup[sp.HybridId];
				const hydratedHybrid = hydrateHybrid(hybridLookupItem, treatmentLookup, materialLookup);
				const hybridDefaultTreatment = autoSelectTreatmentForHybrid(hydratedHybrid, hybridLookup, brandLookup);
				const selectedTreatmentObject = Object.values(hydratedHybrid.Treatments).find(t => t.TreatmentId === sp.TreatmentId);
				if (selectedTreatmentObject && (selectedTreatmentObject.Availability === 'None' || selectedTreatmentObject.Availability === 'Unknown'))
				{
					// The selected treatment is no longer available, so we need to update it to the default treatment
					clonedSp.TreatmentId = hybridDefaultTreatment?.TreatmentId;
				}
				return products.find(p => matchAssignmentToProduct(clonedSp, p));
			}
			catch (error) 
			{
				setError(error);
			}

		}
			// And key by series
		).filter(p => p !== undefined) as IProductIdentifier[], p => p.SeriesId);
	}, [cropPlan, products]);

	// Create the summary (aggregate totals) of the current seed set.
	const productSummary = useMemo(() => 
	{
		if (!cropPlan || !products.length)
		{
			return [];
		}

		// We need to combine:
		// Products that are assigned to fields already
		const productsWithAssignments = cropPlan.Fields.flatMap(f => f.SeedAssignments);

		// With products that were recommended (included in the portfolio) but not already assigned
		const recommendedSeriesWithoutAssignments = cropPlan.Portfolio.filter(
			sp => !productsWithAssignments.find(pwa => hybridLookup[pwa.HybridId]?.SeriesId === sp.SeriesId)
		).map(sp => recommendedProducts[sp.SeriesId]);

		// Combine the products that are assigned with the recommended products that are not assigned (differences are likey in the treatment)
		const allRelevantProductAssignments = [
			...productsWithAssignments.filter(p => p.HybridId).map(pa => ({
				product: products.find(p => matchAssignmentToProduct(pa, p)),
				area: pa.AppliedAcres,
				rate: pa.Rate
			})),
			...recommendedSeriesWithoutAssignments.map(p => ({
				product: p,
				area: 0,
				rate: 0
			}))
		].filter(p => p.product);

		const groupedProducts = groupBy(allRelevantProductAssignments, p => `${p.product!.HybridId}-${p.product!.TreatmentId}`);

		return Object.values(groupedProducts).map(ap => ({
			product: ap[0].product!,
			color: '',
			area: RoundAcres(ap.reduce((prev, cur) => cur.area + prev, 0)),
			quantity: ap.reduce((prev, cur) => (cur.rate * cur.area) + prev, 0)
		}));

	}, [cropPlan, products, recommendedProducts]);

	// We store overall summary and update it instead of using the calculated version above so that we can persist certain things,
	// like the assigned color.
	const [summary, setSummary] = useState<typeof productSummary>([]);
	useEffect(() => 
	{
		setSummary([]);
	}, [cropId]);
	let changedSummary = false;
	let updatedSummary = cloneDeep(summary);

	// Compare our just-calculated summary data to our stored one to see if we need to update it
	const priorLength = updatedSummary.length;
	const cropColorList = FieldPlanProductColorsList[cropId];
	for (const productSummarized of productSummary) 
	{
		const unusedColors = cropColorList.filter(color => !updatedSummary.map(p => p.color).includes(color));
		const existingSummary = updatedSummary.find(s => matchProductIdentity(s.product, productSummarized.product));
		const calculatedQuantity = (!productSummarized.area) ? 0 : CalculateBags(productSummarized.quantity, CropConfig()[cropId].seedsPerBag);
		if (!existingSummary) 
		{
			// Not in our list yet, create a new entry for it
			const nextAvailableColor = unusedColors.length ? unusedColors[0] : cropColorList[(updatedSummary.length) % cropColorList.length];
			updatedSummary.push(
				{
					product: productSummarized.product,
					color: nextAvailableColor,
					area: productSummarized.area,
					quantity: calculatedQuantity
				}
			);
			changedSummary = true;
		}
		else if (existingSummary.area !== productSummarized.area || existingSummary.quantity !== calculatedQuantity) 
		{
			// Update the area on the existing entry
			existingSummary.area = productSummarized.area;
			existingSummary.quantity = calculatedQuantity;
			changedSummary = true;
		}
	}

	// We also need to remove anything that no longer has assignments
	updatedSummary = updatedSummary.filter((summary) => productSummary.find(ps => ps.product.HybridId === summary.product.HybridId
		&& ps.product.TreatmentId === summary.product.TreatmentId)
	);
	
	if (updatedSummary.length != priorLength || changedSummary) 
	{
		// Something changed, store the result.
		setSummary(orderBy(updatedSummary, s => -s.area));
	}

	const derivedData = useMemo(() => ({
		products,
		recommendedProducts,
		summary
	}), [products, recommendedProducts, summary]);

	useEffect(() => 
	{
		// When something changes, update the redux store.
		dispatch(setDerivedData(derivedData));
	}, [derivedData, dispatch]);

	return {derivedData, error};
};
